import { Component, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

// Last-resort boundary above Suspense. Without this, a rejection from the
// auth-init promise (consumed via React's `use()`) produces an unrecoverable
// blank screen. Here we at least show the user something actionable.
export class RootErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: unknown) {
    console.error('RootErrorBoundary caught:', error, info)
  }

  override render() {
    if (!this.state.error) return this.props.children
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
        <h1 className="text-lg font-medium">Something went wrong.</h1>
        <p className="max-w-md text-sm text-muted-foreground">
          We couldn&rsquo;t finish loading the dashboard. Try reloading — if it
          keeps happening, sign in again.
        </p>
        <div className="flex gap-2">
          <Button onClick={() => window.location.reload()}>Reload</Button>
          <Button
            variant="outline"
            onClick={() => {
              window.location.href = '/login'
            }}
          >
            Go to sign in
          </Button>
        </div>
      </div>
    )
  }
}
